/**
 * The combined job page's decisions (phase 3 spec 2026-09-26 §4), pure: the
 * stage tracker, the one next thing, which tabs, and who opens a project as
 * a job. The page only renders what these return.
 */
import { canSeeCosts, isKeeper } from './roles';

export const STEP_LABEL = {
  scanned: 'Scanned',
  designed: 'Designed',
  sent: 'Estimate sent',
  signed: 'Signed',
  building: 'Building',
  closed: 'Closed out',
  done: 'Done',
};

const PLAN_STEPS = {
  money: ['scanned', 'designed', 'signed', 'building', 'closed'],
  design: ['scanned', 'designed', 'sent', 'signed', 'done'],
};
// the steps a design project answers; a job without one has neither
const DESIGN_STEPS = ['scanned', 'designed'];
const FINISHED = ['completed', 'closed'];

/**
 * Whether a job is done — THE rule, for the tracker, the Jobs list and the
 * stage pill: the design plan's hand mark on the design project
 * (`projects/{id}.stage`), or its work finished (the money plan's stages).
 */
export const isJobDone = ({ job, project }) =>
  project?.stage === 'done' || FINISHED.includes(job?.status);

/**
 * Each step's fact, from what the page already loaded. `signed` is the
 * page's one answer (`contractState` in jobMoneyModel), never re-read here.
 */
export const stepFacts = ({
  structureCount,
  renderingCount,
  estimates,
  job,
  signed,
  projectStage,
}) => ({
  scanned: structureCount > 0,
  // an estimate SAVED from the take-off carries its structure; one typed by
  // hand does not, and is not a design
  designed:
    renderingCount > 0 || estimates.some((e) => Boolean(e.structure_id)),
  // a withdrawn estimate is not one waiting on the customer
  sent: estimates.some((e) => Boolean(e.sent_at) && e.status !== 'void'),
  signed,
  building: FINISHED.includes(job?.status),
  closed: job?.status === 'closed',
  done: isJobDone({ job, project: { stage: projectStage } }),
});

/** The tracker: the first step not done is current. */
export const jobSteps = ({ money, hasProject, facts, progressPercent }) => {
  const keys = PLAN_STEPS[money ? 'money' : 'design'].filter(
    (key) => hasProject || !DESIGN_STEPS.includes(key)
  );
  const current = keys.find((key) => !facts[key]) ?? null;
  return keys.map((key) => {
    const state = facts[key] ? 'done' : key === current ? 'current' : 'todo';
    const note =
      key === 'building' && state === 'current' && progressPercent != null
        ? `${Math.round(progressPercent)}% done`
        : null;
    return { key, label: STEP_LABEL[key], state, note };
  });
};

/** The one next thing, in words, and the tab that does it. */
export const nextStep = ({ steps, facts, customerName }) => {
  const current = steps.find((s) => s.state === 'current');
  if (!current) return null;
  const who = customerName || 'the customer';
  switch (current.key) {
    case 'scanned':
      return {
        text: 'Scan the rooms with the Housfully iPhone app',
        tab: 'design',
      };
    case 'designed':
      return {
        text: 'Design it, then save the estimate from the take-off',
        tab: 'design',
      };
    case 'sent':
      return { text: 'Finish and send the estimate', tab: 'contract' };
    case 'signed':
      return facts.sent
        ? {
            text: `Follow up with ${who} — the estimate is not signed yet`,
            tab: 'contract',
          }
        : { text: 'Finish and send the estimate', tab: 'contract' };
    case 'building':
      return {
        text: 'Build it — record progress as the work gets done',
        tab: 'progress',
      };
    case 'closed':
      return { text: 'Close out after the last payment', tab: 'billing' };
    case 'done':
      return {
        text: 'Mark the job done when the work is finished',
        tab: 'overview',
      };
    default:
      return null;
  }
};

export const TAB_LABEL = {
  overview: 'Overview',
  design: 'Design',
  contract: 'Proposals & contracts',
  billing: 'Billing',
  quotes: 'Quotes',
  costs: 'Costs',
  progress: 'Progress',
  punch: 'Punch list',
  receipts: 'Receipts',
  // the notes log grew into the daily log (20260928000600)
  notes: 'Daily log',
  // the job's to-dos (20260929000600)
  tasks: 'To-dos',
  files: 'Photos & files',
};

/**
 * Which tabs, by plan and role (spec §4 "Tabs", "Roles"). The money tabs
 * wait for a books job: a design project with none has nothing to bill.
 * Every job keeps a notes log (phase 5a) and to-dos (2026-09-29), so
 * Notes and To-dos wait for one too.
 *
 * The crew (`field`) on a job get their phone view instead (phase 5 §5a):
 * Progress · Receipts · Notes · To-dos, then the design and the photos they
 * had. Progress and receipts are the money plan's (the database refuses
 * both without it), so the design plan's crew keep Notes and To-dos —
 * `crewPlanNote` says why, on the daily log they land on. On a design project with no job there is nothing to record
 * against, and the crew keep what they had.
 *
 * The punch list (2026-10-04) is every role's on both plans, once there is a job.
 */
export const jobTabs = ({ money, role, hasProject, hasJob }) => {
  const crew = role === 'field';
  const design = hasProject ? ['design'] : [];
  if (crew && hasJob) {
    return [
      ...(money ? ['progress', 'receipts'] : []),
      'notes',
      'tasks',
      'punch',
      ...design,
      'files',
    ];
  }
  const moneyTabs = money && hasJob && !crew;
  return [
    'overview',
    ...design,
    ...(crew ? [] : ['contract']),
    ...(moneyTabs ? ['billing'] : []),
    // vendor quotes (2026-09-28): the keepers'; recording one is owner / office
    ...(moneyTabs && isKeeper(role) ? ['quotes'] : []),
    ...(moneyTabs && canSeeCosts(role) ? ['costs'] : []),
    ...(moneyTabs ? ['progress'] : []),
    ...(hasJob ? ['punch', 'notes', 'tasks'] : []),
    'files',
  ];
};

/**
 * What the crew are told on the design plan, where their view is Notes and
 * To-dos: why progress and receipts are not there. Null for everyone else.
 */
export const crewPlanNote = ({ money, role, companyName }) =>
  role === 'field' && !money
    ? `Progress and receipts come with the Backoffice Ops Plan. ${companyName} is on the design plan, so only the daily log and the to-dos are kept here.`
    : null;

/**
 * Whether `/project/:id` opens as a job: a contractor's own project, one
 * they edit, or one of their organization's — never a project someone
 * outside their organization shared with them (that stays today's page).
 */
export const opensAsJob = ({ contractor, level, inProjectOrg }) =>
  contractor && (level === 'owner' || level === 'editor' || inProjectOrg);

/**
 * `/project/:id` asked for as the design project — its scans, plans and
 * notes — rather than the job, whoever is asking. The classic projects list
 * (`/projects`) links here, so a scan there is one click from its floor plan
 * for a contractor too, as it is for everyone else.
 */
export const designPagePath = (projectId) =>
  `/project/${projectId}?view=design`;

export const asksForDesignPage = (search) =>
  new URLSearchParams(search).get('view') === 'design';

export const jobTitle = ({ job, project }) => job?.name || project?.name || '';

// what happened to the job itself — its stage, its link, its people — and
// never its money
const CREW_EVENT = /^(job|member)\./;

/** The activity a role sees: crew (`field`) never see the money. */
export const eventsFor = ({ role, events }) =>
  role === 'field' ? events.filter((e) => CREW_EVENT.test(e.kind)) : events;

/**
 * Whether New invoice may make this invoice the whole job (20260929000900):
 * a job with no estimate and no invoice yet (void ones aside), no contract,
 * and someone who may write one (`editor`: owner or office).
 */
export const wholeJobOffered = ({ editor, signed, estimates, invoices }) =>
  Boolean(editor) &&
  !signed &&
  estimates.every((e) => e.status === 'void') &&
  invoices.every((i) => i.status === 'void');

// the estimates that point at a design, in the order they speak for it
const DESIGN_RANK = { accepted: 0, sent: 1, draft: 2 };

/**
 * The scans the Overview's design card opens (2026-10-19): one row per scan
 * a live estimate of the job was priced from — signed first, then sent, then
 * drafts, the newest first within each — so the design the card opens is the
 * one that was priced, not merely the latest scan. A scan with no such
 * estimate is left to the Design tab (`others`); with none at all, the card
 * offers the newest scan, marked as only that (`newest`).
 *
 * `structures` newest first (getStructuresByProjectId); `estimates` newest
 * first (listEstimates). A void or replaced estimate, a typed one and one
 * whose scan is not in the project are no row's.
 */
export const designRows = ({ structures, estimates }) => {
  const scans = new Map(structures.map((s) => [s.id, s]));
  const ranked = estimates
    .filter((e) => e.status in DESIGN_RANK && scans.has(e.structure_id))
    // a stable sort: within a status, still newest first
    .sort((a, b) => DESIGN_RANK[a.status] - DESIGN_RANK[b.status]);
  const byScan = new Map();
  for (const e of ranked) {
    if (!byScan.has(e.structure_id)) {
      byScan.set(e.structure_id, {
        structure: scans.get(e.structure_id),
        estimates: [],
        newest: false,
      });
    }
    byScan.get(e.structure_id).estimates.push(e);
  }
  const rows = byScan.size
    ? [...byScan.values()]
    : structures.slice(0, 1).map((s) => ({
        structure: s,
        estimates: [],
        newest: true,
      }));
  return { rows, others: structures.length - rows.length };
};
